import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getServices } from '../api';

const curatedServices = [
  {
    name: 'Consulting',
    description: 'Expert HR and business consulting to help companies refine hiring strategy, team structure, and people operations.'
  },
  {
    name: 'Recruitment',
    description: 'Targeted talent search and end-to-end placement support for fast, accurate hiring across multiple roles.'
  },
  {
    name: 'Staffing',
    description: 'Flexible staffing support for permanent, temporary, and contract positions with faster turnaround.'
  },
  {
    name: 'Training',
    description: 'Practical skill development sessions that prepare candidates and teams for stronger performance.'
  },
  {
    name: 'Profile Writing',
    description: 'Professional resume and profile writing support to improve clarity, positioning, and interview reach.'
  },
  {
    name: 'Resume/CV Creation',
    description: 'Modern, ATS-friendly resume and CV creation tailored to job role, industry expectations, and recruiter review.'
  }
];

const processSteps = [
  {
    title: 'Understand Requirement',
    description: 'We begin by identifying hiring goals, candidate expectations, and the right service mix.'
  },
  {
    title: 'Execute With Structure',
    description: 'Each service is handled through a clear workflow so delivery stays fast, practical, and consistent.'
  },
  {
    title: 'Support Until Outcome',
    description: 'We stay involved through shortlist, communication, and readiness so the result is usable, not partial.'
  }
];

const serviceAccent = ['01', '02', '03', '04', '05', '06', '07', '08'];

const mergeServices = (apiServices) => {
  const byName = new Map();

  [...apiServices, ...curatedServices].forEach((service) => {
    const normalizedName = String(service.name || '').trim();
    if (!normalizedName) return;

    const key = normalizedName.toLowerCase();
    const normalizedDescription = String(service.description || '').trim();
    if (!byName.has(key)) {
      byName.set(key, {
        name: normalizedName,
        description: normalizedDescription || 'Professional service support from Dani Job Club.'
      });
      return;
    }

    const existing = byName.get(key);
    if (!existing.description && normalizedDescription) {
      byName.set(key, { ...existing, description: normalizedDescription });
    }
  });

  return Array.from(byName.values());
};

const Services = () => {
  const [services, setServices] = useState(curatedServices);

  useEffect(() => {
    let isMounted = true;

    getServices()
      .then((res) => {
        if (!isMounted) return;
        const apiServices = Array.isArray(res.data) ? res.data : [];
        setServices(mergeServices(apiServices));
      })
      .catch(() => {
        if (isMounted) {
          setServices(mergeServices([]));
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-black py-8 md:py-10">
      <div className="w-[calc(100%-1.5rem)] md:w-[calc(100%-3rem)] xl:w-[calc(100%-12rem)] mx-auto space-y-8">
        <section className="relative overflow-hidden rounded-[36px] border border-yellow-300 bg-gradient-to-br from-black via-neutral-900 to-yellow-500 px-6 py-10 md:px-10 md:py-14">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,214,10,0.32),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.12),transparent_30%)]" />
          <div className="relative grid grid-cols-1 xl:grid-cols-[1.3fr_0.9fr] gap-8 items-center">
            <div>
              <p className="text-sm font-extrabold tracking-[0.3em] uppercase text-yellow-300">Our Services</p>
              <h1 className="mt-4 text-4xl md:text-5xl xl:text-6xl font-extrabold leading-tight text-white">
                Structured services for hiring, candidate growth, and career presentation.
              </h1>
              <p className="mt-5 max-w-3xl text-base md:text-lg leading-8 text-white/85">
                Dani Job Club combines recruitment support, staffing, profile development, and career-ready documentation in one cleaner service experience.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/?register=1"
                  className="inline-flex items-center justify-center rounded-xl bg-yellow-400 px-6 py-3 text-black font-extrabold hover:bg-yellow-300 transition"
                >
                  Get Started
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl border border-white/40 bg-white/10 px-6 py-3 text-white font-bold hover:bg-white hover:text-black transition"
                >
                  Talk to Us
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-1 gap-4">
              <div className="rounded-[28px] border border-white/15 bg-white/10 backdrop-blur px-5 py-5 text-white">
                <p className="text-xs uppercase tracking-[0.22em] text-yellow-300 font-bold">Coverage</p>
                <p className="text-4xl font-extrabold mt-3">{services.length}</p>
                <p className="text-sm text-white/80 mt-2">Service tracks available for employers and job seekers.</p>
              </div>
              <div className="rounded-[28px] border border-white/15 bg-white/10 backdrop-blur px-5 py-5 text-white">
                <p className="text-xs uppercase tracking-[0.22em] text-yellow-300 font-bold">Focus</p>
                <p className="text-2xl font-extrabold mt-3">Career + Hiring</p>
                <p className="text-sm text-white/80 mt-2">Built for both talent readiness and business hiring outcomes.</p>
              </div>
              <div className="rounded-[28px] border border-white/15 bg-white/10 backdrop-blur px-5 py-5 text-white">
                <p className="text-xs uppercase tracking-[0.22em] text-yellow-300 font-bold">New Add-on</p>
                <p className="text-2xl font-extrabold mt-3">Resume/CV Creation</p>
                <p className="text-sm text-white/80 mt-2">A dedicated service for stronger personal presentation and shortlist quality.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[32px] border border-gray-200 bg-white px-5 py-8 md:px-8 shadow-[0_24px_70px_rgba(0,0,0,0.06)]">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
            <div>
              <p className="text-sm font-extrabold tracking-[0.28em] uppercase text-yellow-500">Service Lines</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold">Designed with proper structure and clearer focus</h2>
            </div>
            <p className="max-w-2xl text-gray-600 leading-7">
              Each service is presented as a focused offering so users can quickly understand what Dani Job Club provides and where it fits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {services.map((service, index) => (
              <article
                key={service.name}
                className="group relative overflow-hidden rounded-[28px] border border-gray-200 bg-gradient-to-b from-white to-yellow-50 p-6 shadow-[0_16px_45px_rgba(0,0,0,0.06)] transition hover:-translate-y-1 hover:border-yellow-300 hover:shadow-[0_24px_55px_rgba(250,204,21,0.18)]"
              >
                <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-yellow-300/20 blur-2xl" />
                <div className="relative">
                  <div className="mb-5 flex items-center justify-between gap-4">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-black text-sm font-extrabold tracking-[0.18em] text-yellow-400">
                      {serviceAccent[index] || String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="rounded-full border border-yellow-300 bg-yellow-100 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.2em] text-black">
                      Active Service
                    </span>
                  </div>

                  <h3 className="text-2xl font-extrabold leading-tight text-black">{service.name}</h3>
                  <p className="mt-4 text-gray-700 leading-7 min-h-[110px]">{service.description}</p>

                  <div className="mt-6 flex items-center justify-between gap-4 border-t border-gray-200 pt-4">
                    <div>
                      <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-gray-500">Best For</p>
                      <p className="mt-1 text-sm font-bold text-black">Business teams and job seekers</p>
                    </div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center rounded-xl bg-yellow-400 px-4 py-2.5 text-sm font-extrabold text-black hover:bg-yellow-300 transition"
                    >
                      Enquire
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6">
          <div className="rounded-[32px] border border-black bg-black px-6 py-8 text-white md:px-8">
            <p className="text-sm font-extrabold tracking-[0.28em] uppercase text-yellow-400">Why It Works</p>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight">One page, clearer service understanding.</h2>
            <p className="mt-4 text-white/80 leading-7">
              The service page now highlights structured offerings, a stronger visual hierarchy, and a clear addition for Resume/CV Creation.
            </p>

            <div className="mt-8 space-y-4">
              {processSteps.map((step, index) => (
                <div key={step.title} className="rounded-[24px] border border-white/10 bg-white/5 px-5 py-4">
                  <p className="text-xs font-extrabold tracking-[0.2em] uppercase text-yellow-300">Step 0{index + 1}</p>
                  <h3 className="mt-2 text-xl font-extrabold">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/80">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[32px] border border-yellow-300 bg-yellow-50 px-6 py-8 md:px-8">
            <p className="text-sm font-extrabold tracking-[0.28em] uppercase text-yellow-600">Popular Add-on</p>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-black">Resume/CV Creation is now part of the service portfolio.</h2>
            <p className="mt-4 text-gray-700 leading-7">
              This additional service helps candidates present their experience more clearly with better formatting, role-specific wording, and stronger first-level screening performance.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-[24px] bg-white px-5 py-5 border border-yellow-200">
                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-gray-500">Included</p>
                <p className="mt-3 text-lg font-extrabold text-black">Role-focused formatting</p>
                <p className="mt-2 text-sm text-gray-700 leading-6">Cleaner layouts, better content flow, and more professional positioning for candidate profiles.</p>
              </div>
              <div className="rounded-[24px] bg-white px-5 py-5 border border-yellow-200">
                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-gray-500">Outcome</p>
                <p className="mt-3 text-lg font-extrabold text-black">Stronger shortlist visibility</p>
                <p className="mt-2 text-sm text-gray-700 leading-6">Built to improve readability and help candidates present value faster to employers.</p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/?register=1"
                className="inline-flex items-center justify-center rounded-xl bg-yellow-400 px-6 py-3 text-black font-extrabold hover:bg-yellow-300 transition"
              >
                Register Now
              </Link>
              <Link
                to="/jobs"
                className="inline-flex items-center justify-center rounded-xl border border-black px-6 py-3 text-black font-bold hover:border-yellow-500 hover:text-yellow-700 transition"
              >
                Explore Jobs
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Services;
